// External libraries
import React, { useState } from 'react';
import { useStoreState } from 'easy-peasy';
import {
  FloatingLabelSelect,
  LoadingElement,
  PageInfoMessage,
} from '@checkout/react-components';

// Internal modules
import { ARIA_ATTRIBUTES } from '../shared';
import { containerStyles, pageInfoStyles } from './styles';
import { getCountryChangeHandler } from './handlers';
import { features, getContent, getLocale, useMountLogging } from '@/lib';

const CountryDropdown = () => {
  useMountLogging('CountrySelector');
  const { country: globalsCountry, locale } = getLocale();
  const hasRestriction = features.isActive('blockNonDomesticPayer');
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const countriesList = useStoreState(({ api }) => {
    return api.griffinMetadata.territories[locale];
  });

  // We need to use local state for this component since we debounce the change
  // for non-Mac OS machines on the web. This is done so keyboard users don't
  // trigger a change every time they press the down/up arrow.
  const [country, setCountry] = useState(globalsCountry);

  if (!countriesList?.length) {
    return <LoadingElement />;
  }

  const isDisabled = isFormSubmitting || hasRestriction;
  const handleCountryChange = getCountryChangeHandler(isDisabled);

  return (
    <>
      {hasRestriction && (
        <PageInfoMessage
          id="country-info-message"
          message={getContent('messages.blockNonDomesticPayer')}
          styleOverrides={pageInfoStyles}
        />
      )}

      <FloatingLabelSelect
        changeHandler={(e, toCountry) => {
          setCountry(toCountry);
          handleCountryChange(e, toCountry);
        }}
        disabled={isDisabled}
        floatingLabelContainerStyleOverrides={hasRestriction && containerStyles}
        id="country"
        isChangeBlocked={hasRestriction}
        isRequired={true}
        label={getContent('fieldLabels.country')}
        name="country"
        selectOptions={countriesList}
        value={country}
        {...(hasRestriction && ARIA_ATTRIBUTES)}
      />
    </>
  );
};

export { CountryDropdown };
